// 📁 File: src/components/DashboardSidebar.jsx
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import {
  LayoutDashboard,
  LogOut,
  AppWindow,
  User,
  ChevronDown,
  Palette,
  X
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

import i18n from '../i18n';

const API = process.env.REACT_APP_API_BASE;

export default function DashboardSidebar({ onCloseMobile, activeSection }) {
  const { logout, user: authUser } = useContext(AuthContext);
  const [user, setUser] = useState(authUser);
  const [appsMenu, setAppsMenu] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${API}/api/users/me`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const data = await res.json();
        if (res.ok) setUser(data);
      } catch (err) {
        console.error('Failed to fetch user details', err);
      }
    };

    if (!authUser?.planId) {
      fetchUser();
    }
  }, [authUser?.planId]);

  useEffect(() => {
    if (!user?.planId) return;
    setAppsLoading(true);
    fetch(`${API}/api/app-hierarchy?planId=${user.planId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setAppsMenu(data);
        } else {
          console.error('Unexpected response:', data);
          setAppsMenu([]); // fallback to empty
        }
      })
      .catch((err) => console.error('Failed to fetch apps menu', err))
      .finally(() => setAppsLoading(false));
  }, [user?.planId]);
  

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'ar', label: 'العربية' },
    { code: 'hi', label: 'हिन्दी' }
  ];

  const isSubUser = user?.userType === 'sub';

  const accountChildren = [
    { label: t('profile'), path: '/account/profile' },
    !isSubUser && { label: t('manageteam'), path: '/account/manage-team' },
    !isSubUser && { label: t('subscription_plan'), path: '/account/subscription' },
    !isSubUser && { label: t('billing_history'), path: '/account/billing' },
    !isSubUser && { label: t('payment_methods'), path: '/account/paymentmethods' }
  ].filter(Boolean);

  const menuItems = [
    {
      label: t('dashboard'),
      icon: LayoutDashboard,
      path: '/dashboard'
    },
    {
      label: t('ai_apps'),
      icon: AppWindow,
      dynamic: true
    },
    {
      label: t('brand_profile'),
      icon: Palette,
      path: '/brand-profile'
    },
    {
      label: t('my_account'),
      icon: User,
      children: accountChildren
    }
  ];

  return (
    <div className="w-64 bg-white text-gray-800 min-h-screen h-full shadow-md flex flex-col justify-between">
      <div>
        <div className="flex justify-end md:hidden p-4">
          <button onClick={onCloseMobile}>
            <X className="w-5 h-5 text-gray-600 hover:text-primary" />
          </button>
        </div>

        <img src="/ennoverse.png" alt="Ennoverse Logo" className="h-8 w-auto mx-4 my-4" />

        <nav className="px-4 mt-4 space-y-2">
          {menuItems.map((item) => (
            item.dynamic ? (
              <Disclosure key={item.label}>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex items-center justify-between w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm font-medium">
                      <span className="flex items-center gap-2">
                        <item.icon className="w-4 h-4" /> {t(item.label)}
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
                    </Disclosure.Button>
                    <Disclosure.Panel className="pl-2">
                      {appsLoading ? (
                        <div className="text-xs text-gray-500 px-3 py-2">Loading apps...</div>
                      ) : (
                        appsMenu.filter((cat) => (cat.apps?.length || cat.subcategories?.length)).map((cat) => (
                          <Disclosure key={cat.category}>
                            {({ open: subOpen }) => (
                              <>
                                <Disclosure.Button className="flex items-center justify-between w-full text-left px-3 py-1 hover:bg-gray-100 rounded text-xs font-semibold text-gray-600">
                                  <span>{cat.category}</span>
                                  <ChevronDown className={`w-3 h-3 transition-transform ${subOpen ? 'rotate-180' : ''}`} />
                                </Disclosure.Button>
                                <Disclosure.Panel className="ml-4 space-y-1">
                                  {cat.apps?.map((app) => (
                                    <Link key={app.routeKey} to={`/app/${app.routeKey}`} className="block text-xs text-gray-700 hover:text-blue-600">
                                      • {app.name}
                                    </Link>
                                  ))}
                                  {cat.subcategories?.map((sub) => (
                                    <Disclosure key={sub.name}>
                                      {({ open: deepOpen }) => (
                                        <>
                                          <Disclosure.Button className="flex items-center justify-between w-full text-left px-2 py-1 hover:bg-gray-100 rounded text-xs text-gray-600">
                                            <span>{sub.name}</span>
                                            <ChevronDown className={`w-3 h-3 ${deepOpen ? 'rotate-180' : ''}`} />
                                          </Disclosure.Button>
                                          <Disclosure.Panel className="ml-3 space-y-1 mt-1">
                                            {sub.apps.map((app) => (
                                              <Link key={app.routeKey} to={`/app/${app.routeKey}`} className="block text-xs text-gray-700 hover:text-blue-600 ml-2">
                                                • {app.name}
                                              </Link>
                                            ))}
                                          </Disclosure.Panel>
                                        </>
                                      )}
                                    </Disclosure>
                                  ))}
                                </Disclosure.Panel>
                              </>
                            )}
                          </Disclosure>
                        ))
                      )}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ) : item.children ? (
              <Disclosure key={item.label} defaultOpen={item.label === t('my_account') && activeSection === 'account'}>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex items-center justify-between w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm font-medium">
                      <span className="flex items-center gap-2">
                        <item.icon className="w-4 h-4" /> {item.label}
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
                    </Disclosure.Button>
                    <Disclosure.Panel className="pl-4 space-y-1 mt-1">
                      {item.children.map((child) => (
                        <Link key={child.path} to={child.path} className={`block text-xs px-2 py-1 rounded hover:text-blue-600 ${location.pathname === child.path ? 'text-blue-600 font-semibold' : 'text-gray-700'}`}>
                          • {child.label}
                        </Link>
                      ))}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ) : (
              <Link key={item.path} to={item.path} className={`flex items-center gap-2 px-3 py-2 rounded text-sm hover:bg-gray-100 ${location.pathname === item.path ? 'text-blue-600 font-semibold' : 'text-gray-700'}`}>
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            )
          ))}
        </nav>
      </div>

      <div className="px-4 py-4 space-y-4">
        <select
          value={i18n.language}
          onChange={async (e) => {
            const selectedLang = e.target.value;
            i18n.changeLanguage(selectedLang);
            localStorage.setItem('lang', selectedLang);

            await fetch(`${API}/api/auth/language`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({ language: selectedLang })
            });

            window.location.reload();
          }}
          className="w-full p-2 border border-gray-300 rounded text-sm"
        >
          <option value="en">English</option>
          <option value="ar">العربية</option>
          <option value="hi">हिन्दी</option>
        </select>

        <button
          onClick={() => {
            logout();
            navigate('/login');
          }}
          className="w-full bg-red-500 hover:bg-red-600 py-2 rounded text-white flex items-center justify-center text-sm"
        >
          <LogOut className="inline w-4 h-4 mr-2" /> {t('logout')}
        </button>
      </div>
    </div>
  );
}
